import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PrinterIcon, ReceiptIcon, ArrowsClockwiseIcon, TrashIcon } from "@phosphor-icons/react";
import { inr } from "./posHelpers";

export function TableHistory({ activeId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // table_order_id -> detail
  const canDelete = user?.role === "admin";

  const load = () => {
    if (!activeId) return;
    setLoading(true);
    api.get("/tables/history", { params: { property_id: activeId, limit: 100 } })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const toggle = async (to) => {
    if (expanded[to.id]) {
      const { [to.id]: _, ...rest } = expanded;
      setExpanded(rest);
      return;
    }
    try {
      const r = await api.get(`/tables/${to.id}/detail`);
      setExpanded({ ...expanded, [to.id]: r.data });
    } catch (e) { toast.error("Could not load rounds"); }
  };

  const printReceipt = (id) => window.open(`/pos/print/receipt/${id}`, "receipt", "width=380,height=700");
  const printKot = (orderId) => window.open(`/pos/print/kot/${orderId}`, "kotPrint", "width=380,height=600");

  const deleteBill = async (to) => {
    if (!window.confirm(`Void table bill ${to.id.slice(-8).toUpperCase()} (Table ${to.table_number})?\n\nAll rounds will be removed and stock will be restored. This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/tables/history/${to.id}`);
      toast.success(`Bill voided · ${r.data.rounds_deleted} round(s) removed, stock restored`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card>
      <div className="p-4 border-b border-border flex justify-between items-center">
        <div>
          <p className="label-overline">Closed bills — reprint</p>
          <h3 className="font-display text-lg">Recent restaurant bills</h3>
          <p className="text-xs text-muted-foreground">Any settled table order can be reprinted (receipt or per-round KOT).</p>
        </div>
        <Button size="sm" variant="ghost" onClick={load}><ArrowsClockwiseIcon size={14} className="mr-1" />Refresh</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="p-3">Bill #</th>
              <th className="p-3">Table</th>
              <th className="p-3">Closed at</th>
              <th className="p-3">Guests</th>
              <th className="p-3">Payment</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground italic">{loading ? "Loading…" : "No closed bills yet today."}</td></tr>
            )}
            {rows.map((to) => (
              <React.Fragment key={to.id}>
                <tr className="border-t border-border/60" data-testid={`table-history-row-${to.id}`}>
                  <td className="p-3 font-mono-plex text-xs">{to.id.slice(-8).toUpperCase()}</td>
                  <td className="p-3"><Badge variant="outline">{to.table_number}</Badge> <span className="text-[10px] text-muted-foreground ml-1">{to.section}</span></td>
                  <td className="p-3 text-xs">{new Date(to.closed_at).toLocaleString("en-IN")}</td>
                  <td className="p-3">{to.guest_count}</td>
                  <td className="p-3 text-xs uppercase">
                    {to.final_payment_mode?.replace("_", " ")}
                    {to.posted_to_reservation && <Badge className="ml-1 text-[9px]" variant="outline">→ ROOM</Badge>}
                  </td>
                  <td className="p-3 text-right font-mono-plex">{inr(to.final_total)}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => printReceipt(to.id)} data-testid={`table-history-reprint-${to.id}`}>
                        <ReceiptIcon size={12} className="mr-1" /> Receipt
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggle(to)} data-testid={`table-history-toggle-${to.id}`}>
                        {expanded[to.id] ? "Hide" : "Rounds"}
                      </Button>
                      {canDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deleteBill(to)}
                          title="Void this bill and restore stock (admin only)"
                          data-testid={`table-history-delete-${to.id}`}
                        >
                          <TrashIcon size={14} className="text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {expanded[to.id] && (
                  <tr className="bg-muted/20">
                    <td colSpan={7} className="p-3">
                      <div className="space-y-2">
                        {(expanded[to.id].rounds || []).map((r) => (
                          <div key={r.id} className="flex justify-between items-center rounded border border-border/60 p-2 bg-card">
                            <div>
                              <p className="text-xs font-mono-plex">{r.order_no}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {(r.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono-plex">{inr(r.total)}</span>
                              <Button size="sm" variant="outline" onClick={() => printKot(r.id)} data-testid={`table-history-kot-${r.order_no}`}>
                                <PrinterIcon size={12} className="mr-1" /> KOT
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
